/**
 * DatePicker: a field that opens a month calendar. Values are plain calendar days ("2026-12-10"), never shifted
 * through a time zone.
 *
 *   <DatePicker id={id} value="2026-12-10" onChange={setDate} min={resolveDate("today")} max="2027-10-24" />
 *   <DatePicker id={id} range value="2024-01-01 2024-06-30" onChange={setRange} maxDays={3650} />
 *   <DatePicker id={id} value={start} span={7} onChange={setStart} />      highlights the 7 days a run covers
 *
 * Keyboard (in the calendar grid): arrows move a day or a week, Home/End go to the start or end of the week,
 * PageUp/PageDown change the month (with Shift, the year), Enter or Space picks. Escape closes and focus returns to
 * the field. Days outside min and max are disabled. "Today" jumps to and picks today when it is allowed.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import { fieldClasses } from "@/components/ui/TextField";
import { useEscape, useLayer } from "@/hooks/useOverlay";
import { useFloating } from "./useFloating";

const pad = (n: number) => String(n).padStart(2, "0");
export const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dateOf = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
export const addDays = (iso: string, n: number) => {
  const d = dateOf(iso);
  d.setDate(d.getDate() + n);
  return isoOf(d);
};
const addMonths = (iso: string, n: number) => {
  const d = dateOf(iso);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return isoOf(d);
};
export const todayIso = () => isoOf(new Date());
const VALID = /^\d{4}-\d{2}-\d{2}$/;

/** A template bound ("today", "today+365", "today-1" or a date) as a date in the viewer's own calendar. */
export function resolveDate(bound?: string): string | undefined {
  if (!bound) return undefined;
  const m = bound.trim().match(/^today\s*(?:([+-])\s*(\d+))?$/i);
  if (m) return addDays(todayIso(), m[1] ? (m[1] === "-" ? -1 : 1) * Number(m[2]) : 0);
  return VALID.test(bound) ? bound : undefined;
}

const LONG = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
const SHORT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const MONTH = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
const FULL = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
export const formatDay = (iso: string, style: "long" | "short" = "long") => (VALID.test(iso) ? (style === "long" ? LONG : SHORT).format(dateOf(iso)) : iso);
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface DatePickerProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  /** Pick a start and an end; the value is "start end". */
  range?: boolean;
  maxDays?: number;
  /** Highlight this many days from the chosen date (what a run covers). */
  span?: number;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
  placeholder?: string;
}

export default function DatePicker({ id, value, onChange, min, max, range = false, maxDays, span, required, invalid, describedBy, placeholder }: DatePickerProps) {
  const [startValue, endValue] = range ? value.split(/\s+/) : [value, ""];
  const start = VALID.test(startValue ?? "") ? startValue : "";
  const end = VALID.test(endValue ?? "") ? endValue : "";
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(() => start || clampDay(todayIso(), min, max));
  const [pending, setPending] = useState<string | null>(null); // range: the start picked, waiting for the end
  const [hover, setHover] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const isTop = useLayer(open);
  const floating = useFloating(open, triggerRef, panelRef, { minWidth: 304, matchWidth: false });

  const close = (refocus = true) => {
    setOpen(false);
    setPending(null);
    setHover(null);
    if (refocus) triggerRef.current?.focus();
  };
  useEscape(open, () => close(), isTop);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node) && !triggerRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Move keyboard focus to the focused day whenever it changes while open.
  useEffect(() => {
    if (!open) return;
    const el = gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${focus}"]`);
    el?.focus({ preventScroll: true });
  }, [focus, open]);

  const openPanel = () => {
    setFocus(start || clampDay(todayIso(), min, max));
    setOpen(true);
  };

  // While choosing a range end, days past maxDays are off limits.
  const lo = pending && maxDays ? (min && min > addDays(pending, 1 - maxDays) ? min : addDays(pending, 1 - maxDays)) : min;
  const hi = pending && maxDays ? (max && max < addDays(pending, maxDays - 1) ? max : addDays(pending, maxDays - 1)) : max;
  const disabled = (iso: string) => (lo !== undefined && iso < lo) || (hi !== undefined && iso > hi);

  const choose = (iso: string) => {
    if (disabled(iso)) return;
    if (!range) {
      onChange(iso);
      close();
      return;
    }
    if (!pending) {
      setPending(iso);
      return;
    }
    const [a, b] = iso < pending ? [iso, pending] : [pending, iso];
    onChange(`${a} ${b}`);
    close();
  };

  const onGridKey = (e: React.KeyboardEvent) => {
    const moves: Record<string, () => string> = {
      ArrowLeft: () => addDays(focus, -1),
      ArrowRight: () => addDays(focus, 1),
      ArrowUp: () => addDays(focus, -7),
      ArrowDown: () => addDays(focus, 7),
      Home: () => addDays(focus, -((dateOf(focus).getDay() + 6) % 7)),
      End: () => addDays(focus, 6 - ((dateOf(focus).getDay() + 6) % 7)),
      PageUp: () => addMonths(focus, e.shiftKey ? -12 : -1),
      PageDown: () => addMonths(focus, e.shiftKey ? 12 : 1),
    };
    if (moves[e.key]) {
      e.preventDefault();
      setFocus(moves[e.key]());
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      choose(focus);
    }
  };

  // The month shown is the focused day's month; weeks start on Monday.
  const weeks = useMemo(() => {
    const d = dateOf(focus);
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7;
    const gridStart = addDays(isoOf(first), -startOffset);
    const days = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const count = Math.ceil((startOffset + days) / 7) * 7;
    const all = Array.from({ length: count }, (_, i) => addDays(gridStart, i));
    return Array.from({ length: count / 7 }, (_, w) => all.slice(w * 7, w * 7 + 7));
  }, [focus]);
  const month = dateOf(focus).getMonth();

  const today = todayIso();
  const spanEnd = span && start ? addDays(start, span - 1) : "";
  const rangeA = range ? (pending ? (hover && hover < pending ? hover : pending) : start) : start;
  const rangeB = range ? (pending ? (hover && hover > pending ? hover : pending) : end) : spanEnd || start;
  const inRange = (iso: string) => !!rangeA && !!rangeB && iso >= rangeA && iso <= rangeB;
  const prevDisabled = !!min && addMonths(focus, -1).slice(0, 7) < min.slice(0, 7);
  const nextDisabled = !!max && addMonths(focus, 1).slice(0, 7) > max.slice(0, 7);

  const label = range
    ? start && end
      ? `${formatDay(start, "short")} to ${formatDay(end, "short")}`
      : ""
    : start
      ? formatDay(start)
      : "";

  return (
    <div className="relative">
      <div className="relative">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-describedby={describedBy}
          onClick={() => (open ? close() : openPanel())}
          onKeyDown={e => {
            if (e.key === "ArrowDown" && !open) {
              e.preventDefault();
              openPanel();
            }
          }}
          className={`${fieldClasses(invalid)} flex h-9 items-center gap-2 px-3 text-left text-base sm:text-sm ${!required && value ? "pr-9" : ""} ${open ? "border-primary ring-3 ring-primary/20" : ""}`}
        >
          <Icon icon="lucide:calendar" className="shrink-0 text-base text-fg-muted" aria-hidden="true" />
          <span className={`min-w-0 flex-1 truncate ${label ? "text-fg" : "text-fg-muted"}`}>{label || placeholder || (range ? "Pick a start and end date" : "Pick a date")}</span>
          {span && start && span > 1 ? <span className="shrink-0 text-xs text-fg-muted">to {formatDay(spanEnd, "short").replace(/, \d{4}$/, "")}</span> : null}
        </button>
        {!required && value && (
          <button
            type="button"
            aria-label="Clear date"
            onClick={() => onChange("")}
            className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-fg-muted outline-none hover:bg-surface-muted hover:text-fg focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Icon icon="lucide:x" className="text-sm" aria-hidden="true" />
          </button>
        )}
      </div>

      {open &&
        createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          style={floating}
          className="overflow-y-auto rounded-card border border-line bg-raised p-3 shadow-popover"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              aria-label="Previous month"
              disabled={prevDisabled}
              onClick={() => setFocus(f => addMonths(f, -1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-control text-fg-secondary outline-none hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-30"
            >
              <Icon icon="lucide:chevron-left" className="text-base" aria-hidden="true" />
            </button>
            <h3 id={titleId} className="text-sm font-semibold text-fg" aria-live="polite">
              {MONTH.format(dateOf(focus))}
            </h3>
            <button
              type="button"
              aria-label="Next month"
              disabled={nextDisabled}
              onClick={() => setFocus(f => addMonths(f, 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-control text-fg-secondary outline-none hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-30"
            >
              <Icon icon="lucide:chevron-right" className="text-base" aria-hidden="true" />
            </button>
          </div>

          <div ref={gridRef} role="grid" aria-labelledby={titleId} onKeyDown={onGridKey} onMouseLeave={() => setHover(null)}>
            <div role="row" className="grid grid-cols-7">
              {WEEKDAYS.map((w, i) => (
                <span key={w} role="columnheader" aria-label={WEEKDAY_NAMES[i]} className="py-1 text-center text-xs font-medium text-fg-muted">
                  {w}
                </span>
              ))}
            </div>
            {weeks.map(week => (
              <div role="row" key={week[0]} className="grid grid-cols-7">
                {week.map(iso => {
                  const d = dateOf(iso);
                  const other = d.getMonth() !== month;
                  const off = disabled(iso);
                  const isEdge = iso === rangeA || iso === rangeB;
                  const within = inRange(iso);
                  return (
                    <div role="gridcell" key={iso} aria-selected={isEdge || undefined} className={`py-0.5 ${within && !isEdge ? "bg-primary-subtle" : ""} ${within && iso === rangeA && rangeA !== rangeB ? "rounded-l-control bg-primary-subtle" : ""} ${within && iso === rangeB && rangeA !== rangeB ? "rounded-r-control bg-primary-subtle" : ""}`}>
                      <button
                        type="button"
                        data-day={iso}
                        tabIndex={iso === focus ? 0 : -1}
                        aria-disabled={off || undefined}
                        aria-label={`${FULL.format(d)}${iso === today ? ", today" : ""}${off ? ", not available" : ""}`}
                        aria-current={iso === today ? "date" : undefined}
                        onClick={() => {
                          setFocus(iso);
                          choose(iso);
                        }}
                        onMouseEnter={() => pending && setHover(iso)}
                        className={[
                          "mx-auto flex h-9 w-9 items-center justify-center rounded-control text-sm tabular-nums outline-none transition-colors",
                          "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-raised",
                          isEdge ? "bg-primary font-semibold text-white" : off ? "cursor-not-allowed text-fg-muted/40 line-through decoration-fg-muted/30" : "text-fg hover:bg-surface-muted",
                          other && !isEdge && !off ? "text-fg-muted" : "",
                          iso === today && !isEdge ? "font-semibold underline decoration-primary decoration-2 underline-offset-4" : "",
                        ].join(" ")}
                      >
                        {d.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="mt-2 flex items-center justify-between gap-2 border-t border-line pt-2">
            <p className="min-w-0 text-xs text-fg-muted" aria-live="polite">
              {range ? (pending ? `From ${formatDay(pending, "short")}. Now pick the end.` : maxDays ? `Up to ${maxDays.toLocaleString("en-US")} days` : "Pick the start, then the end") : span && span > 1 && start ? `${span} days from the date` : ""}
            </p>
            <button
              type="button"
              disabled={disabled(today)}
              onClick={() => {
                setFocus(today);
                choose(today);
              }}
              className="shrink-0 rounded-control px-2 py-1 text-13 font-medium text-primary-text outline-none hover:bg-primary-subtle focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
            >
              Today
            </button>
          </div>
        </div>,
          document.body,
        )}
    </div>
  );
}

function clampDay(iso: string, min?: string, max?: string) {
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}
